import { useAuth } from '../lib/auth';
import { useMyGroups, useTrip } from '../lib/db';
import { formatDay, money } from '../lib/money';
import { coverersOf, myUnit, sharesFor } from '../lib/split';
import { Empty, Glyph, Loading, Page } from '../components/ui';
import { Link } from 'react-router-dom';
import type { Group, TripData } from '../types';

/**
 * A feed across every trip. Each trip subscribes separately, which keeps this
 * simple and costs nothing once the local cache is warm.
 */
export default function Activity() {
  const { user } = useAuth();
  const { groups, loading } = useMyGroups(user?.uid);

  if (loading && !groups.length) return <Page title="Activity" noFab><Loading /></Page>;

  return (
    <Page wordmark="Activity" subtitle="everything across your trips" noFab>
      {groups.length === 0 ? (
        <Empty title="Nothing yet">
          Activity from all your trips collects here once you've added an expense.
        </Empty>
      ) : (
        groups.map((g) => <TripActivity key={g.id} group={g} uid={user!.uid} />)
      )}
    </Page>
  );
}

function TripActivity({ group, uid }: { group: Group; uid: string }) {
  const { data } = useTrip(group.id);
  if (!data) return null;

  const { units, expenses, settlements } = data as TripData;
  const mine = myUnit(units, uid);
  const name = (id: string) => units.find((u) => u.id === id)?.name ?? 'Someone';

  const items = [
    ...expenses.map((e) => ({ date: e.date, kind: 'e' as const, e })),
    ...settlements.map((s) => ({ date: s.date, kind: 's' as const, s }))
  ].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 12);

  if (!items.length) return null;

  return (
    <>
      <div className="day">{group.name}</div>
      <div className="list">
        {items.map((it) =>
          it.kind === 'e' ? (
            <Link className="row" key={it.e.id} to={`/t/${group.id}/e/${it.e.id}`}>
              <Glyph name={it.e.category} />
              <span className="main">
                <span className="t">{it.e.desc}</span>
                <span className="s">
                  {formatDay(it.e.date)} · {[
                    mine && it.e.paidByUnitId === mine.id ? 'you' : name(it.e.paidByUnitId),
                    ...coverersOf(units, it.e).map((u) => u.name)
                  ].join(' + ')} paid
                </span>
              </span>
              <span className="end">
                <span className="t amt">{money(it.e.amountMinor, group.currency)}</span>
                <span className="s amt">
                  {mine ? money(sharesFor(units, it.e)[mine.id] ?? 0, group.currency) : ''}
                </span>
              </span>
            </Link>
          ) : (
            <div className="row" key={it.s.id}>
              <Glyph name="settle" tint />
              <span className="main">
                <span className="t">{name(it.s.fromUnitId)} paid {name(it.s.toUnitId)}</span>
                <span className="s">{formatDay(it.s.date)}{it.s.method ? ` · ${it.s.method}` : ''}</span>
              </span>
              <span className="end">
                <span className="t amt pos">{money(it.s.amountMinor, group.currency)}</span>
              </span>
            </div>
          )
        )}
      </div>
    </>
  );
}
